import { useAnalytics } from '@backstage/core-plugin-api';
import { useEffect, useRef } from 'react';
import { useFirstMountState } from 'react-use/esm/useFirstMountState';
import { useSearch } from '@backstage/plugin-search-react';

import { NEO4J_SEARCH_EVENT_NAME } from '../const';

/**
 * Capture search result event
 *
 * The `search` event `value` (number of results) is not available in
 * deployments due to the permissions framework.
 *
 * Therefore, we need to create our own event name to distinguish it
 * and call `useAnalytics.captureEvent` to capture the event.
 *
 * @see https://backstage.io/docs/plugins/analytics/#key-events
 */
export function useCaptureSearchEvent() {
  const { result, term } = useSearch();
  const analytics = useAnalytics();
  const isFirstMount = useFirstMountState();

  const prevTerm = useRef<string>(term);
  const hasSearched = useRef<boolean>(false);

  useEffect(() => {
    if (result.error) {
      return;
    }

    if (result.loading && !isFirstMount) {
      hasSearched.current = true;
      prevTerm.current = term;

      return;
    }

    // Only run after the search has finished and the term has changed
    if (prevTerm.current === term && hasSearched.current) {
      analytics.captureEvent(NEO4J_SEARCH_EVENT_NAME, term, {
        /**
         * Use `results.length` instead of `numberOfResults`
         * because the latter is not available in deployments
         */
        value: result.value?.results.length,
      });
    }
  }, [analytics, isFirstMount, result, term]);
}
